import { useRef } from 'react'

import { Tag } from '@tribeplatform/gql-client/types'
import { useCreateTag, useUpdateTag } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import { Form } from '../Form/index.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

export const TagFormModal = ({
  tag,
  open,
  onClose,
}: {
  tag?: Tag
  open: boolean
  onClose: () => void
}) => {
  const inputRef = useRef<HTMLInputElement>()
  const { mutateAsync: createTag, isLoading: isCreating } = useCreateTag({
    fields: 'basic',
  })
  const { mutateAsync: updateTag, isLoading: isUpdating } = useUpdateTag()
  const isNew = !tag?.id
  const { $t } = useI18n()

  return (
    <Modal open={open} onClose={onClose} initialFocus={inputRef}>
      <Modal.Header
        title={
          isNew ? (
            <T defaultMessage="Create a new {TAG}" id="TagForm.Title.Create" />
          ) : (
            <T defaultMessage="Edit {TAG}" id="TagForm.Title.Edit" />
          )
        }
      />
      <Modal.Content>
        <Form
          defaultValues={tag}
          onSubmit={async data => {
            const { title, description, slug } = data
            const input = {
              title,
              description,
              slug,
            }

            if (tag?.id) {
              return updateTag({
                id: tag.id,
                input,
              }).then(() => {
                onClose()
              })
            }

            return createTag({
              input,
            }).then(() => {
              onClose()
            })
          }}
        >
          <div className="flex flex-col space-y-5">
            <Form.Input
              ref={inputRef}
              label={$t({
                defaultMessage: 'Title',
                id: 'Generics.Title',
              })}
              name="title"
              validation={{
                required: $t({
                  defaultMessage: 'Please enter a title',
                  id: 'TagForm.Input.RequiredFields.Title',
                }),
              }}
            />
            {!isNew && (
              <Form.Input
                label={$t({
                  defaultMessage: 'Slug',
                  id: 'Generics.Slug',
                })}
                name="slug"
                validation={{
                  required: $t({
                    defaultMessage: 'Please enter a slug',
                    id: 'TagForm.Input.RequiredFields.Slug',
                  }),
                }}
              />
            )}
            <Form.Textarea
              label={
                <T defaultMessage="Description" id="Generics.Description" />
              }
              name="description"
              placeholder={$t({
                defaultMessage: 'What is this {TAG} about?',
                id: 'TagForm.Input.Description.Placeholder',
              })}
            />
            <Form.Actions>
              <Button
                variant="primary"
                type="submit"
                loading={isCreating || isUpdating}
              >
                {isNew ? (
                  <T defaultMessage="Create" id="Generics.Create" />
                ) : (
                  <T defaultMessage="Update" id="Generics.Update" />
                )}
              </Button>
              <Button variant="secondaryNeutral" onClick={onClose}>
                <T defaultMessage="Cancel" id="Generics.Cancel" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  )
}
